import { useQuery } from 'react-query';
import { fetchMostLoggedMetrics } from 'pages/Dashboard/services/tracking.services';
import { trackingUrls } from 'utils/apiUrls';
import { MoodOptions, MoodTrackingAverageResponse } from 'pages/Dashboard/types/moods.types';
import { MoodFactorDomainsResponse, MostLoggedMetricsResponse } from 'pages/Dashboard/types/waypoints.types';
import { getMoodOptions, TimeFrameOptions } from 'pages/Dashboard/utils/trackingUtils';
import React from 'react';
import { useHttp } from 'hooks/use-fetch';

export enum MostLoggedMetrics {
  MOOD = 'MOOD',
  SITUATIONS = 'SITUATIONS',
  DOMAINS = 'DOMAINS',
}

interface UseMostLoggedMetricsProps {
  providerId: string | undefined;
  patientId: string | undefined;
  selectedTimeFrame: string;
  isNewVersionEnabled: boolean;
  moodId: string;
  currentMoodData: MoodTrackingAverageResponse;
  previousMoodData: MoodTrackingAverageResponse;
}

function useMostLoggedMetrics({
  providerId,
  patientId,
  selectedTimeFrame,
  isNewVersionEnabled,
  moodId,
  currentMoodData,
  previousMoodData,
}: UseMostLoggedMetricsProps) {
  const { http } = useHttp();

  const fetchMostLoggedMoodsResult = useQuery(
    [
      `${trackingUrls.mostLoggedMetrics.queryUrl}`,
      providerId,
      patientId,
      selectedTimeFrame,
      MostLoggedMetrics.MOOD,
    ],
    {
      queryFn: fetchMostLoggedMetrics(
        http.get,
      ),
      enabled: !!providerId && !!patientId && isNewVersionEnabled,
    },
  );

  const fetchMostLoggedSituationResult = useQuery(
    [
      `${trackingUrls.mostLoggedMetrics.queryUrl}`,
      providerId,
      patientId,
      selectedTimeFrame,
      moodId,
      'mood',
    ],
    {
      queryFn: fetchMostLoggedMetrics(
        http.get as (url: string, params?: unknown) => Promise<unknown>,
      ),
      enabled: !!providerId && !!patientId && isNewVersionEnabled,
    },
  );

  // Additional API calls for 6 months timeframe
  const fetchMostLoggedMoods6MResult = useQuery(
    [
      `${trackingUrls.mostLoggedMetrics.queryUrl}`,
      providerId,
      patientId,
      TimeFrameOptions.LAST_6_MONTHS,
      MostLoggedMetrics.MOOD,
    ],
    {
      queryFn: fetchMostLoggedMetrics(
        http.get as (url: string, params?: unknown) => Promise<unknown>,
      ),
      enabled: !!providerId && !!patientId && isNewVersionEnabled,
    },
  );

  const fetchMostLoggedSituation6MResult = useQuery(
    [
      `${trackingUrls.mostLoggedMetrics.queryUrl}`,
      providerId,
      patientId,
      TimeFrameOptions.LAST_6_MONTHS,
      moodId,
      'mood',
    ],
    {
      queryFn: fetchMostLoggedMetrics(
        http.get as (url: string, params?: unknown) => Promise<unknown>,
      ),
      enabled: !!providerId && !!patientId && isNewVersionEnabled,
    },
  );

  const mostLoggedMetrics = React.useMemo(
    () => ({
      mood: (fetchMostLoggedMoodsResult?.data as MostLoggedMetricsResponse)?.percents || [],
      factors:
        (fetchMostLoggedSituationResult?.data as MoodFactorDomainsResponse)
          ?.moodFactorDomains || [],
      moodsLoading: fetchMostLoggedMoodsResult?.isLoading,
      factorsLoading: fetchMostLoggedSituationResult?.isLoading,
    }),
    [
      fetchMostLoggedMoodsResult?.data,
      fetchMostLoggedMoodsResult?.isLoading,
      fetchMostLoggedSituationResult?.isLoading,
      fetchMostLoggedSituationResult?.data,
    ],
  );

  // Only return true if there is 6 month data for either mood or situation
  const hasMostLoggedMetrics = React.useMemo(() => {
    const moods6M = (
      fetchMostLoggedMoods6MResult?.data as MostLoggedMetricsResponse
    )?.percents || [];
    const factors6M = (
      fetchMostLoggedSituation6MResult?.data as MoodFactorDomainsResponse
    )?.moodFactorDomains || [];
    return moods6M.length > 0 || factors6M.length > 0;
  }, [fetchMostLoggedMoods6MResult?.data, fetchMostLoggedSituation6MResult?.data]);

  const moodOptions: MoodOptions[] = React.useMemo(
    () => getMoodOptions(currentMoodData.moods, previousMoodData.moods, isNewVersionEnabled),
    [currentMoodData.moods, isNewVersionEnabled, previousMoodData.moods],
  );

  return {
    fetchMostLoggedMoodsResult,
    fetchMostLoggedSituationResult,
    mostLoggedMetrics,
    hasMostLoggedMetrics,
    moodOptions,
  };
}

export default useMostLoggedMetrics;
